import type { FC, ReactNode } from 'react'
import type { DashboardNotification } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'

import { NOTIFICATIONS } from '@/data/activity'

type DashboardContextValue = {
    collapsed: boolean
    setCollapsed: (collapsed: boolean) => void
    toggleCollapsed: () => void
    mobileNavOpen: boolean
    setMobileNavOpen: (open: boolean) => void
    commandOpen: boolean
    setCommandOpen: (open: boolean) => void
    shortcutsOpen: boolean
    setShortcutsOpen: (open: boolean) => void
    isMac: boolean
    notifications: DashboardNotification[]
    unreadCount: number
    markAsRead: (id: string) => void
    markAllAsRead: () => void
}

interface DashboardProviderProps {
    children: ReactNode
}

const SIDEBAR_KEY = 'arunika-sidebar-collapsed'

const noop = (): void => undefined

const DashboardContext = createContext<DashboardContextValue>({
    collapsed: false,
    setCollapsed: noop,
    toggleCollapsed: noop,
    mobileNavOpen: false,
    setMobileNavOpen: noop,
    commandOpen: false,
    setCommandOpen: noop,
    shortcutsOpen: false,
    setShortcutsOpen: noop,
    isMac: false,
    notifications: NOTIFICATIONS,
    unreadCount: 0,
    markAsRead: noop,
    markAllAsRead: noop
})

const isTypingTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false
    return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

export const DashboardProvider: FC<DashboardProviderProps> = ({ children }): ReactNode => {
    const [collapsed, setCollapsedState] = useState(false)
    const [mobileNavOpen, setMobileNavOpen] = useState(false)
    const [commandOpen, setCommandOpen] = useState(false)
    const [shortcutsOpen, setShortcutsOpen] = useState(false)
    const [isMac, setIsMac] = useState(false)
    const [notifications, setNotifications] = useState<DashboardNotification[]>(NOTIFICATIONS)

    useEffect(() => {
        try {
            if (window.localStorage.getItem(SIDEBAR_KEY) === '1') setCollapsedState(true)
        }
        catch {
            // Storage unavailable: keep the sidebar expanded
        }

        setIsMac(/mac|iphone|ipad/i.test(window.navigator.userAgent))
    }, [])

    const setCollapsed = useCallback((value: boolean) => {
        setCollapsedState(value)

        try {
            window.localStorage.setItem(SIDEBAR_KEY, value ? '1' : '0')
        }
        catch {
            // Ignore: the choice still applies for this visit
        }
    }, [])

    const toggleCollapsed = useCallback(() => {
        setCollapsed(!collapsed)
    }, [collapsed, setCollapsed])

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            const key = event.key.toLowerCase()

            if ((event.metaKey || event.ctrlKey) && key === 'k') {
                event.preventDefault()
                setCommandOpen((open) => !open)
                return
            }

            if ((event.metaKey || event.ctrlKey) && key === 'b') {
                event.preventDefault()
                toggleCollapsed()
                return
            }

            if (key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey && !isTypingTarget(event.target)) {
                event.preventDefault()
                setCommandOpen(true)
            }
        }

        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [toggleCollapsed])

    const markAsRead = useCallback((id: string) => {
        setNotifications((items) => items.map((item) => (item.id === id ? { ...item, read: true } : item)))
    }, [])

    const markAllAsRead = useCallback(() => {
        setNotifications((items) => items.map((item) => ({ ...item, read: true })))
    }, [])

    const unreadCount = notifications.filter((item) => !item.read).length

    const value = useMemo(() => ({
        collapsed,
        setCollapsed,
        toggleCollapsed,
        mobileNavOpen,
        setMobileNavOpen,
        commandOpen,
        setCommandOpen,
        shortcutsOpen,
        setShortcutsOpen,
        isMac,
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead
    }), [collapsed, setCollapsed, toggleCollapsed, mobileNavOpen, commandOpen, shortcutsOpen, isMac, notifications, unreadCount, markAsRead, markAllAsRead])

    return (
        <DashboardContext.Provider value={value}>
            {children}
        </DashboardContext.Provider>
    )
}

export const useDashboard = (): DashboardContextValue => useContext(DashboardContext)
